import { Badge, getMaxBadgeSizes } from '@/components/badge/badge.component';
import { Body2, Body3, H3 } from '@/components/typography';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { FieldSummaryType } from '@/modules/Report/Report.schema';

type Props = {
  customerId?: string;
  projectId?: string;
  farmId: string;
  title?: string;
  fieldSummaries: FieldSummaryType[];
};

export const FieldSummaryTable = ({ customerId, projectId, farmId, title, fieldSummaries }: Props) => {
  const href = customerId && projectId && farmId ? `customers/${customerId}/projects/${projectId}/farms/${farmId}` : '';

  const shouldShowColumn = (key: keyof FieldSummaryType): boolean =>
    fieldSummaries.some(
      (field: FieldSummaryType) => field[key] && typeof field[key] === 'object' && 'value' in field[key] && field[key].value !== null
    );

  const columns = ['pH', 'pOlsen', 'pMM', 'k', 'kMpl', 'mg', 'mgMpl', 'li', 'n', 'ca', 'caMpl'] as const;

  const showColumns = Object.fromEntries(columns.map((col) => [col, shouldShowColumn(col)]));

  const columnHeaders: Record<(typeof columns)[number], string> = {
    pH: 'pH',
    pOlsen: 'P Olsen (ppm)',
    pMM: 'P Mod. Morgan (mg/l)',
    k: 'K (ppm)',
    kMpl: 'K (mg/l)',
    mg: 'Mg (ppm)',
    mgMpl: 'Mg (mg/l)',
    li: 'Li (ppm)',
    n: 'N (ppm)',
    ca: 'Ca (ppm)',
    caMpl: 'Ca (mg/l)',
  };

  const visibleColumns = columns.filter((col) => showColumns[col]);
  const dynamicColumnCount = 4 + visibleColumns.length + 1;
  const columnStyle = { width: `${100 / dynamicColumnCount}%` };
  const badgeSizesPerCol = getMaxBadgeSizes(fieldSummaries, visibleColumns);
  const tableHeightStyle = fieldSummaries.length < 10 ? 'max-h-[700px]' : 'h-[700px]';

  return (
    <div className={cn('flex-col mt-7')}>
      <div className={cn('flex items-center justify-between')}>
        {title && <H3>{title}</H3>}
        <div className={cn('flex gap-5')}>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('w-4 h-4 rounded-lg bg-red')} />
            <Body3>Investigate</Body3>
          </div>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('w-4 h-4 rounded-lg bg-yellow')} />
            <Body3>Review</Body3>
          </div>
          <div className={cn('flex items-center gap-2')}>
            <div className={cn('w-4 h-4 rounded-lg bg-green')} />
            <Body3>Continue Rotational Monitoring</Body3>
          </div>
        </div>
      </div>
      <Body3 className="mt-3 mb-5 text-neutral-500">Where no colour is shown, no relevant benchmarks are available</Body3>
      <div className={cn('flex flex-col')}>
        <div className={cn(`overflow-y-auto border border-neutral-200 rounded-lg`, tableHeightStyle)}>
          <Table className="min-w-full">
            <TableHeader className="sticky-table-header">
              <TableRow>
                <TableHead style={columnStyle} className={cn('border border-neutral-25 text-left')}>
                  Field Number
                </TableHead>
                <TableHead style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                  SOC %
                </TableHead>
                <TableHead style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                  SOC (t/ha)
                </TableHead>
                <TableHead style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                  SOM %
                </TableHead>
                {columns.map(
                  (col) =>
                    showColumns[col] && (
                      <TableHead key={col} style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                        {columnHeaders[col]}
                      </TableHead>
                    )
                )}
              </TableRow>
            </TableHeader>
            <TableBody className="border-t-2">
              {fieldSummaries.map((fieldSummary, index) => (
                <TableRow
                  key={index}
                  className={cn('cursor-pointer transition-all duration-500', 'hover:bg-neutral-200 hover:shadow-sm', 'hover:font-medium')}
                  onClick={() => {
                    if (fieldSummary.fieldId) {
                      window.location.href = `/${href}/fields/${fieldSummary.fieldId}`;
                    }
                  }}
                >
                  <TableCell style={columnStyle} className={cn('border border-neutral-25 text-left font-semibold')}>
                    {fieldSummary.fieldNumber}
                  </TableCell>
                  <TableCell style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                    {fieldSummary.socPercentage} %
                  </TableCell>
                  <TableCell style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                    {fieldSummary.socTPerHa}
                  </TableCell>
                  <TableCell style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                    {fieldSummary.somPercentage} %
                  </TableCell>
                  {columns.map(
                    (col) =>
                      showColumns[col] && (
                        <TableCell key={col} style={columnStyle} className={cn('border border-neutral-25 text-right')}>
                          {fieldSummary[col]?.value ? (
                            <span className="inline-block">
                              <Badge
                                value={fieldSummary[col]?.value}
                                color={fieldSummary[col].color}
                                size={badgeSizesPerCol[col]}
                                textAlign="right"
                              />
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      )
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Body2 className="w-full text-left my-5 text-neutral-600">
          Detailed results for each field are shown in the remainder of this report
        </Body2>
      </div>
    </div>
  );
};
